import React from 'react'
import {RouteComponentProps, withRouter} from 'react-router'
import {observer} from 'mobx-react'
import {observable} from 'mobx'
import {Instance, types} from 'mobx-state-tree'
import {getSiteImagesQuery_getSiteImages} from '../../schema/schemaTypes'
import styled from 'styled-components'
import Box, {Row} from '../../atoms/Box'
import {media} from '../../utils/media'
import {env} from '../../utils/env'
import {Alert} from '../../atoms/Alert'
// @ts-ignore
import Slider from 'react-slick'
import exitFullScreenImage from './exitFullscreen.svg'
import fullScreenImage from './fullscreen.svg'
import iframeBackground from './iframeBackground.png'

const lang = process.env.REACT_APP_LANGUAGE === 'es' ? 'es' : 'en'

const translations = {
  'es': {
    hospital: 'Hospital',
    tour0p1: 'Este edificio se llama el hospital. Aquí es donde tendrás tu procedimiento.',
    tour0p2: 'Hay muchas personas dentro del hospital que ayudarán a cuidarte mientras estés aquí para tu procedimiento.',
    tour0p3: 'Cuando llegues al hospital, tú y tus padres se registrarán en la recepción.',
    waitingRoom: 'Sala de Espera',
    tour1p1: 'Después de registrarte, probablemente tengas que esperar un poco en esta sala de espera.',
    tour1p2: 'Hay muchas cosas divertidas qué hacer en la sala de espera. Recuerde, ¡no esperarás aquí mucho tiempo!',
    getReady: 'Cuarto de Preparación',
    tour2p1: 'Una vez que llamen tu nombre, una enfermera u otra persona en el hospital te llevará a prepararte para tu procedimiento. Irás a un cuarto especial llamado "cuarto de preparación".',
    tour2p2: 'Aquí es donde te cambiarás a un pijama especial y verás a tus doctores y enfermeras. Tu enfermera escuchará tu corazón. La enfermera también te alistará para tu procedimiento. Tus padres te acompañarán al cuarto de preparación.',
    sleepRoom: 'Cuarto de Dormir',
    tour3p1: 'Después de caminar o te anden por los pasillos, llegarás al cuarto donde tendrás tú procedimiento. A veces lo llamamos el “cuarto de dormir", porque es aquí donde irás a dormir para tú procedimiento.',
    tour3p2: 'Los doctores y las enfermeras estarán contigo en el cuarto de dormir. Te pedirán que subas a una cama diferente para tú procedimiento. Habrá luces brillantes y máquinas en el cuarto de dormir, tal como se ve en estas fotos.',
    wakeUpRoom: 'Cuarto de Despertar',
    tour4p1: 'Después de tú procedimiento, te despertarás en un nuevo lugar. Y adivina cómo se llama... ¡así es! El cuarto de despertar.',
    tour4p2: 'Es posible que te sientas un poco raro cuando te despiertes de tú procedimiento, pero las enfermeras y los doctores están allí para ayudarte y cuidarte. ¡Tus padres también estarán allí! Habrá máquinas que hagan algo de ruido en el cuarto de despertar, como las que ves en el recorrido.',
    error: 'Error has occurred, try again',
  },
  'en': {
    hospital: 'Hospital',
    tour0p1: 'This building is called the hospital. This is where you will\n' +
      '            have your procedure.',
    tour0p2: 'There are a lot of people inside the hospital that will help take care of you while you\n' +
      '            are here for your procedure.',
    tour0p3: 'When you get to the hospital, you and your parents will check in at the front desk.',
    waitingRoom: 'Waiting Room',
    tour1p1: 'After you check in, you might have to wait a little in this waiting room.',
    tour1p2: 'There are lots of fun things to do in the waiting room, but remember, you won\'t be waiting',
    getReady: 'Get Ready Room',
    tour2p1: 'Once your name is called, a nurse or someone else in the hospital will take you to get ready for your\n' +
      '            procedure. You will go to a special room called the "get ready room"',
    tour2p2: 'This is where you will get changed into special pajamas and see your doctors and nurses. Your nurse\n' +
      '            will listen to your heart and get you all set for your procedure. Your parents will go with you to the\n' +
      '            get ready room.',
    sleepRoom: 'Sleep Room',
    tour3p1: 'After your walk or ride through the hallways, you will get to the room where you will have your procedure.  Sometimes we call this the "sleep room," because this is where you will go to sleep for your procedure.',
    tour3p2: 'Doctors and nurses will be with you in the sleep room and you will be asked to get on a different bed for your procedure. There will be bright lights and machines in the sleep room, just like you see in these pictures.',
    wakeUpRoom: 'Wake Up Room',
    tour4p1: 'After your procedure, you will wake up in a new room. And guess what it\'s called... that\'s right!\n' +
      '            The wake up room.',
    tour4p2: 'You might feel a little funny when you wake up from your procedure but the nurses and doctors are\n' +
      '            there to help you and take care of you. Your parents will be there too! There will be machines that make\n' +
      '            some noise in the wake up room, like the ones you see in the tour.',
    error: 'Error has occurred, please try again',
  },
}

enum SelectedPicture {
  name = 'name',
  picHospitalUri = 'picHospitalUri',
  picWaitingRoomUri = 'picWaitingRoomUri',
  picGetReadyRoomUri = 'picGetReadyRoomUri',
  picSleepRoomUri = 'picSleepRoomUri',
  picWakeUpRoomUri = 'picWakeUpRoomUri',
  religiousText = 'religiousText',
}

const KidsTourModel = types.model(
  {
    name: types.string,
    picHospitalUri: types.string,
    picWaitingRoomUri: types.string,
    picGetReadyRoomUri: types.string,
    picSleepRoomUri: types.string,
    picWakeUpRoomUri: types.string,
    religiousText: types.string,
    selectedPicture: 'picHospitalUri',
    width: types.number,
    height: types.number,
    index: 0,
    fullscreen: false,
  })
  .views(self => ({
    get imageUri() {
      return self[self.selectedPicture]
    },
    get viewIndex() {
      return self.index
    },
    get getWidth() {
      return self.width
    },
  }))
  .actions(self => ({
    changeSelectedPicture(index: number, selectedPicture: SelectedPicture) {
      self.index = index
      self.selectedPicture = selectedPicture
    },
    changeSize(width: number, height: number) {
      self.width = width
      self.height = height
    },
    changeFullscreen() {
      self.fullscreen = !self.fullscreen
    },
  }))

interface IMainProp {
  store: Instance<typeof KidsTourModel>,
}

const ContentView = observer(({store}: IMainProp) => {
  switch (store.index) {
    case 0:
      return (
        <div>
          <H1>{translations[lang].hospital}</H1>
          <P>{translations[lang].tour0p1}</P>
          <P>{translations[lang].tour0p2}</P>
          <P>{translations[lang].tour0p3}</P>
        </div>
      )
    case 1:
      return (
        <div>
          <H1>{translations[lang].waitingRoom}</H1>
          <P>{translations[lang].tour1p1}</P>
          <P>{translations[lang].tour1p2}</P>
        </div>
      )
    case 2:
      return (
        <div>
          <H1>{translations[lang].getReady}</H1>
          <P>{translations[lang].tour2p1}</P>
          <P>{translations[lang].tour2p2}</P>
        </div>
      )
    case 3:
      return (
        <div>
          <H1>{translations[lang].sleepRoom}</H1>
          <P>{translations[lang].tour3p1}</P>
          <P>
            {translations[lang].tour3p2}</P>
        </div>
      )
    case 4:
      return (
        <div>
          <H1>{translations[lang].wakeUpRoom}</H1>
          <P>{translations[lang].tour4p1}</P>
          <P>{translations[lang].tour4p2}</P>
        </div>
      )
    default:
      return <Alert>{translations[lang].error}</Alert>
  }
})

const SelectionRow = observer(({store}: IMainProp) => {
  const item1Selected = store.viewIndex === 0
  const item2Selected = store.viewIndex === 1
  const item3Selected = store.viewIndex === 2
  const item4Selected = store.viewIndex === 3
  const item5Selected = store.viewIndex === 4
  return (
    <Row center style={{flex: 'auto', justifyContent: 'space-evenly', flexWrap: 'wrap'}}>
      <ItemContainer selected={item1Selected}
                     onClick={() => 
                      store.changeSelectedPicture(0, SelectedPicture.picHospitalUri)}>
        <ImgContainer src={store.picHospitalUri} selected={item1Selected}/>
        <Text>{translations[lang].hospital}</Text>
      </ItemContainer>
      <ItemContainer selected={item2Selected}
                     onClick={() => store.changeSelectedPicture(1, SelectedPicture.picWaitingRoomUri)}>
        <ImgContainer src={store.picWaitingRoomUri} selected={item2Selected}/>
        <Text>{translations[lang].waitingRoom}</Text>
      </ItemContainer>
      <ItemContainer selected={item3Selected}
                     onClick={() => store.changeSelectedPicture(2, SelectedPicture.picGetReadyRoomUri)}>
        <ImgContainer src={store.picGetReadyRoomUri} selected={item3Selected}/>
        <Text>{translations[lang].getReady}</Text>
      </ItemContainer>
      <ItemContainer selected={item4Selected}
                     onClick={() => store.changeSelectedPicture(3, SelectedPicture.picSleepRoomUri)}>
        <ImgContainer src={store.picSleepRoomUri} selected={item4Selected}/>
        <Text>{translations[lang].sleepRoom}</Text>
      </ItemContainer>
      <ItemContainer selected={item5Selected}
                     onClick={() => store.changeSelectedPicture(4, SelectedPicture.picWakeUpRoomUri)}>
        <ImgContainer src={store.picWakeUpRoomUri} selected={item5Selected}/>
        <Text>{translations[lang].wakeUpRoom}</Text>
      </ItemContainer>
    </Row>
  )
})

interface ICarouselProp {
  store: Instance<typeof KidsTourModel>,
  width: number,
}

const SelectionRowCarousel = observer(({store, width}: ICarouselProp) => {
  const item1Selected = store.viewIndex === 0
  const item2Selected = store.viewIndex === 1
  const item3Selected = store.viewIndex === 2
  const item4Selected = store.viewIndex === 3
  const item5Selected = store.viewIndex === 4
  const settings = {
    className: 'center',
    infinite: true,
    centerPadding: '60px',
    slidesToShow: width < 330 ? 1 : width < 475 ? 2 : 3,
    swipeToSlide: true,
    speed: 500,
    nextArrow: <img src="https://s3-us-west-1.amazonaws.com/uci-images/images/orangeRightArrow.svg"/>,
    prevArrow: <img src="https://s3-us-west-1.amazonaws.com/uci-images/images/orangeLeftArrow.svg"/>,
  }
  return (
    <Slider {...settings}>
      <ItemContainer selected={item1Selected} carousel={true}
                     onClick={() => store.changeSelectedPicture(0, SelectedPicture.picHospitalUri)}>
        <ImgContainer src={store.picHospitalUri} selected={item1Selected}/>
        <Text>{translations[lang].hospital}</Text>
      </ItemContainer>
      <ItemContainer selected={item2Selected} carousel={true}
                     onClick={() => store.changeSelectedPicture(1, SelectedPicture.picWaitingRoomUri)}>
        <ImgContainer src={store.picWaitingRoomUri} selected={item2Selected}/>
        <Text>{translations[lang].waitingRoom}</Text>
      </ItemContainer>
      <ItemContainer selected={item3Selected} carousel={true}
                     onClick={() => store.changeSelectedPicture(2, SelectedPicture.picGetReadyRoomUri)}>
        <ImgContainer src={store.picGetReadyRoomUri} selected={item3Selected}/>
        <Text>{translations[lang].getReady}</Text>
      </ItemContainer>
      <ItemContainer selected={item4Selected} carousel={true}
                     onClick={() => store.changeSelectedPicture(3, SelectedPicture.picSleepRoomUri)}>
        <ImgContainer src={store.picSleepRoomUri} selected={item4Selected}/>
        <Text>{translations[lang].sleepRoom}</Text>
      </ItemContainer>
      <ItemContainer selected={item5Selected} carousel={true}
                     onClick={() => store.changeSelectedPicture(4, SelectedPicture.picWakeUpRoomUri)}>
        <ImgContainer src={store.picWakeUpRoomUri} selected={item5Selected}/>
        <Text>{translations[lang].wakeUpRoom}</Text>
      </ItemContainer>
    </Slider>
  )
})

interface IProps extends RouteComponentProps {
  images: getSiteImagesQuery_getSiteImages,
  history: any,
}

@observer
class KidsTourViewBase extends React.Component<IProps> {

  @observable public store: Instance<typeof KidsTourModel>
  private iframeView: any

  public constructor(props: any) {
    super(props)
    this.handleWindowSize = this.handleWindowSize.bind(this)
    this.fullScreenButton = this.fullScreenButton.bind(this)
    this.exitFullScreen = this.exitFullScreen.bind(this)
    const {images} = this.props
    const name = images.name || ''
    const picHospitalUri = images.picHospitalUri!
    const picWaitingRoomUri = images.picWaitingRoomUri!
    const picGetReadyRoomUri = images.picGetReadyRoomUri!
    const picSleepRoomUri = images.picSleepRoomUri!
    const picWakeUpRoomUri = images.picWakeUpRoomUri!
    const religiousText = images.religiousText!
    this.store = KidsTourModel.create({
      name,
      picHospitalUri,
      picWaitingRoomUri,
      picGetReadyRoomUri,
      picSleepRoomUri,
      picWakeUpRoomUri,
      religiousText,
      width: window.innerWidth,
      height: window.innerHeight,
    })
  }

  public componentWillMount(): void {
    window.addEventListener('resize', this.handleWindowSize)
  }

  public componentWillUnmount(): void {
    window.removeEventListener('resize', this.handleWindowSize)
  }

  public fullScreenButton() {
    let iframeURL = new URL(`${env.REACT_APP_360_HOST}/index.html`)
    const store: Instance<typeof KidsTourModel> = this.store
    if(window.localStorage.getItem('userType') === 'PARENT_WEBTIPS_ES_EXTENSION') {
      iframeURL = new URL('https://web360.webtips.healthcare/index.html')
     if(store.name.includes('CHOC')){
      iframeURL.searchParams.append('hospital', 'choc')
    }else{
      iframeURL.searchParams.append('hospital', 'seattle')
    }

    if(store.viewIndex === 0){
      iframeURL.searchParams.append('room', 'exterior')
    }else if(store.viewIndex === 1){
      iframeURL.searchParams.append('room', 'waiting')
    }else if(store.viewIndex === 2){
      iframeURL.searchParams.append('room', 'prep')
    }else if(store.viewIndex === 3){
      iframeURL.searchParams.append('room', 'sleep')
    }else if(store.viewIndex === 4){
      iframeURL.searchParams.append('room', 'recovery')
    }

    }
    iframeURL.searchParams.append('img', this.store.imageUri)
    // @ts-ignore
    if(window.ReactNativeWebView) {
      // @ts-ignore
      window.ReactNativeWebView.postMessage(JSON.stringify({name: 'fullscreen', link: iframeURL}), '*')
    }

    if (document.fullscreenEnabled || /* Standard syntax */
      (document as any).webkitFullscreenEnabled || /* Chrome, Safari and Opera syntax */
      (document as any).mozFullScreenEnabled ||/* Firefox syntax */
      (document as any).msFullscreenEnabled/* IE/Edge syntax */) {
      const iframe = this.iframeView
      if (iframe) {
        this.openFullScreen(iframe)
      }
      this.store.changeFullscreen()
    }
  }

  public exitFullScreen() {
    this.store.changeFullscreen()
    const fullScreenElement = document.fullscreenElement || /* Standard syntax */
      (document as any).webkitFullscreenElement || /* Chrome, Safari and Opera syntax */
      (document as any).mozFullScreenElement ||/* Firefox syntax */
      (document as any).msFullscreenElement /* IE/Edge syntax */
    if (fullScreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if ((document as any).mozCancelFullScreen) { /* Firefox */
        (document as any).mozCancelFullScreen()
      } else if ((document as any).webkitExitFullscreen) { /* Chrome, Safari and Opera */
        (document as any).webkitExitFullscreen()
      } else if ((document as any).msExitFullscreen) { /* IE/Edge */
        (document as any).msExitFullscreen()
      }
    }
  }

  public handleWindowSize() {
    this.store.changeSize(window.innerWidth, window.innerHeight)
  }

  public render() {
    const {images} = this.props
    const store: Instance<typeof KidsTourModel> = this.store
    const width = this.store.getWidth
    const fullscreen = this.store.fullscreen
    const iframeURL = window.localStorage.getItem('userType') === 'PARENT_WEBTIPS_ES_EXTENSION' ? new URL('https://web360.webtips.healthcare/index.html') : new URL(`${env.REACT_APP_360_HOST}/index.html`)
    iframeURL.searchParams.append('img', store.imageUri)
    
    if(store.name.includes('CHOC')){
      iframeURL.searchParams.append('hospital', 'choc')
    }else{
      iframeURL.searchParams.append('hospital', 'seattle')
    }

    if(store.viewIndex === 0){
      iframeURL.searchParams.append('room', 'exterior')
    }else if(store.viewIndex === 1){
      iframeURL.searchParams.append('room', 'waiting')
    }else if(store.viewIndex === 2){
      iframeURL.searchParams.append('room', 'prep')
    }else if(store.viewIndex === 3){
      iframeURL.searchParams.append('room', 'sleep')
    }else if(store.viewIndex === 4){
      iframeURL.searchParams.append('room', 'recovery')
    }

    if (width > 640) {
      return (
        <div>
          <Container>
            <ContentView store={store}/>
            <IframeView ref={(iframeView) => {this.iframeView = iframeView}}>
              <Iframe id="iframe" allow = "fullscreen" src={iframeURL.toString()}/>
              {fullscreen && <FullscreenIcon onClick={this.exitFullScreen} src={exitFullScreenImage}/>}
              {!fullscreen && <FullscreenIcon onClick={this.fullScreenButton} src={fullScreenImage}/>}
            </IframeView>
            <IframeBackground src={iframeBackground}/>
            <HomeImage
              id="image"
              src="https://s3-us-west-1.amazonaws.com/uci-images/images/kidsEmptyBackground.svg"
              alt=""/>
            <BearOne src="https://uci-images.s3-us-west-1.amazonaws.com/images/bear4.svg"/>
            <Mooze src="https://uci-images.s3-us-west-1.amazonaws.com/images/mooze.svg"/>
          </Container>
          <Box h={20}/>
          <Container style={{backgroundColor: 'white'}}>
            <SelectionRow store={store}/>
          </Container>
        </div>
      )
    } else {
      return (
        <div>
          <Container style={{backgroundColor: 'white'}}>
            <SelectionRowCarousel store={store} width={width}/>
          </Container>
          <Box h={20}/>
          <Container>
            <ContentView store={store}/>
            <IframeView ref={(iframeView) => {this.iframeView = iframeView}}>
              <Iframe allowFullScreen={true} id="iframe" src={iframeURL.toString()}/>
              {fullscreen && <FullscreenIcon onClick={this.exitFullScreen} src={exitFullScreenImage}/>}
              {!fullscreen && <FullscreenIcon onClick={this.fullScreenButton} src={fullScreenImage}/>}
            </IframeView>
            <IframeBackground src={iframeBackground}/>
            <HomeImage
              id="image"
              src="https://s3-us-west-1.amazonaws.com/uci-images/images/kidsEmptyBackground.svg"
              alt=""/>
          </Container>
        </div>
      )
    }
  }

  private openFullScreen(elem: any) {
    if (elem.requestFullscreen) {
      elem.requestFullscreen()
    } else if (elem.mozRequestFullScreen) { /* Firefox */
      elem.mozRequestFullScreen()
    } else if (elem.webkitRequestFullscreen) { /* Chrome, Safari and Opera */
      elem.webkitRequestFullscreen()
    } else if (elem.msRequestFullscreen) { /* IE/Edge */
      elem.msRequestFullscreen()
    }
  }
}

export const KidsTourView = withRouter(KidsTourViewBase)

const ItemContainer = styled.div<{ selected?: boolean, carousel?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-family: Nunito;
  font-weight: 900;
  color: ${({selected}) => selected ? '#FC9426' : '#285986'}
  ${({carousel}) => carousel ? 'min-width: 100px' : ''}
`

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #C7F0F6;
  top: 20px;
  border-radius: 10px;
  padding-top: 30px;
  padding-bottom: 30px;
  z-index: 1;
  box-shadow: 0 8px 40px 0 rgba(194, 111, 35, 0.2), 0 16px 80px 0 rgba(194, 111, 35, 0.2);
`

const ImgContainer = styled.img<{ selected?: boolean }>`
  border-color: ${({selected}) => selected ? '#FC9426' : 'transparent'}
  border-width: 4px;
  border-radius: 10px;
  width: 93px;
  border-style:solid
`

const Text = styled.text`
  margin-top: 5px;
`

const H1 = styled.h1`
  font-family: Nunito;
  font-weight: 900;
  font-size: 45px;
  color: #12385b;

  ${media.tablet`
    font-size: 54px;
  `}
`

const IframeView = styled.div`
  position: relative;
  border-radius: 20px;
  height: 45vw;
  max-height: 500px;
  margin: 30px 50px 60px 50px;
  overflow: hidden;
  &:-webkit-full-screen {
     width: 100%;
     background: none;
     height: 100%;
     max-height: 100%;
     margin: 0;
 }
`

const FullscreenIcon = styled.img `
  position: absolute;
  right: 20px;
  bottom: 20px;
  background: #FFFFFF;
  border-radius: 3px;
`

const Iframe = styled.iframe`
  border-radius: 20px;
  height: 100%;
  width: 100%;
`

const IframeBackground = styled.img`
  position: absolute;
  bottom: 45px;
  padding: 20px;
  z-index: -1;
`

const P = styled.p`
  font-family: "SF Pro Display";
  font-size: 19px;
  FONT-WEIGHT: 900;
  color: #172129;
  line-height: 24px;
  margin: 10px 0;
  margin: 10px 50px;
`

const HomeImage = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  z-index: -2;
`

const BearOne = styled.img`
  position: absolute;
  bottom: 10px;
  width: 75px;
  top: 250px;
  left: -75px;
`

const Mooze = styled.img`
  position: absolute;
  width: 120px;
  top: 150px;
  right: -66px;
`



// WEBPACK FOOTER //
// ./src/organisms/KidsTour/KidsTourView.tsx